/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../store/authStore';

const REGISTRY_API_URL = process.env.REACT_APP_REGISTRY_API_URL || 'https://cloud.podstack.ai/registry';

const createRegistryApiClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: REGISTRY_API_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    // Prefer the persisted project selection from the auth store, fall back to
    // the localStorage key used by pages that manage project selection locally.
    const projectId =
      useAuthStore.getState().user?.selected_project_id ||
      localStorage.getItem('selectedProjectId');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Only set X-Project-ID if not already set in the request config
    if (projectId && !config.headers['X-Project-ID']) {
      config.headers['X-Project-ID'] = projectId;
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<{ error: string }>) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = `${window.location.origin}/portal/login`;
      }
      const message = error.response?.data?.error || 'An error occurred';
      toast.error(message);
      return Promise.reject(error);
    }
  );

  return client;
};

export const registryApi = createRegistryApiClient();

// Types
export interface Experiment {
  id: string;
  project_id: string;
  backend_experiment_id: string;
  name: string;
  description: string;
  artifact_location: string;
  status: 'active' | 'archived';
  tags: Record<string, string>;
  run_count: number;
  last_run_at: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface Run {
  id: string;
  project_id: string;
  user_id: string;
  experiment_id: string;
  backend_run_id: string;
  name: string;
  status: 'running' | 'completed' | 'failed' | 'killed';
  source_type: string;
  source_name: string;
  artifact_uri: string;
  start_time: number;
  end_time: number | null;
  duration_ms: number;
  tags: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface Metric {
  id: string;
  run_id: string;
  key: string;
  value: number;
  timestamp: number;
  step: number;
  created_at: string;
}

export interface Param {
  id: string;
  run_id: string;
  key: string;
  value: string;
  created_at: string;
}

export interface RegisteredModel {
  id: string;
  project_id: string;
  backend_model_name: string;
  name: string;
  description: string;
  tags: Record<string, string>;
  version_count: number;
  latest_version: number;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ModelVersion {
  id: string;
  model_id: string;
  project_id: string;
  version: number;
  run_id: string;
  source: string;
  status: 'pending' | 'ready' | 'failed';
  stage: 'development' | 'staging' | 'production' | 'archived';
  description: string;
  tags: Record<string, string>;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface StageTransition {
  id: string;
  model_id: string;
  model_version_id: string;
  project_id: string;
  version: number;
  from_stage: string;
  to_stage: string;
  transitioned_by: string;
  comment: string;
  created_at: string;
}

export interface ModelAlias {
  id: string;
  model_id: string;
  project_id: string;
  alias: string;
  model_version_id: string;
  version: number;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface ModelVersionWithRun extends ModelVersion {
  run?: Run;
  experiment?: Experiment;
}

export interface ModelDetail extends RegisteredModel {
  versions: ModelVersionWithRun[];
  aliases: ModelAlias[];
}

export interface ModelStats {
  total_models: number;
  total_versions: number;
  versions_by_stage: Record<string, number>;
  recent_transitions: StageTransition[];
}

export interface MetricHistory {
  key: string;
  values: Metric[];
}

export interface RunComparison {
  runs: Run[];
  metrics: Record<string, Record<string, Metric[]>>; // run_id -> metric_key -> values
  params: Record<string, Record<string, string>>; // run_id -> param_key -> value
  metric_keys: string[];
  param_keys: string[];
  latest_metrics: Record<string, Record<string, number>>; // run_id -> metric_key -> latest_value
}

export interface RunsTimeSeriesPoint {
  date: string;
  count: number;
}

export interface ExperimentWithStats {
  experiment: Experiment;
  completed_runs: number;
  failed_runs: number;
  running_runs: number;
}

export interface ExperimentStats {
  total_experiments: number;
  total_runs: number;
  runs_by_status: Record<string, number>;
  recent_runs: Run[];
  top_experiments: ExperimentWithStats[];
  runs_over_time: RunsTimeSeriesPoint[];
}

export interface RunWithMetrics {
  run: Run;
  latest_metrics: Record<string, number>;
  params: Record<string, string>;
}

export interface ExperimentDetail {
  experiment: Experiment;
  runs: RunWithMetrics[];
  total_runs: number;
  metric_keys: string[];
  param_keys: string[];
  runs_by_status: Record<string, number>;
}

export interface RunDetail {
  run: Run;
  experiment: Experiment;
  metrics: Metric[];
  params: Param[];
  metric_keys: string[];
  latest_metrics: Record<string, number>;
}

// Experiment Service
export const experimentService = {
  list: (params?: { status?: string; offset?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return registryApi.get<{ experiments: Experiment[]; total: number }>(`/api/v1/experiments${query ? `?${query}` : ''}`);
  },

  get: (id: string) => registryApi.get<Experiment>(`/api/v1/experiments/${id}`),

  getDetail: (id: string, params?: { offset?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return registryApi.get<ExperimentDetail>(`/api/v1/experiments/${id}/detail${query ? `?${query}` : ''}`);
  },

  getStats: () => registryApi.get<ExperimentStats>('/api/v1/experiments/stats'),

  create: (data: { name: string; description?: string; tags?: Record<string, string>; project_id?: string }) => {
    const { project_id, ...body } = data;
    const config = project_id ? { headers: { 'X-Project-ID': project_id } } : undefined;
    return registryApi.post<{ experiment: Experiment }>('/api/v1/experiments', body, config);
  },

  update: (id: string, data: { name?: string; description?: string; tags?: Record<string, string> }) =>
    registryApi.put<{ experiment: Experiment }>(`/api/v1/experiments/${id}`, data),

  archive: (id: string) => registryApi.delete(`/api/v1/experiments/${id}`),
};

// Run Service
export const runService = {
  list: (params?: { experiment_id?: string; status?: string; offset?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.experiment_id) queryParams.append('experiment_id', params.experiment_id);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return registryApi.get<{ runs: Run[]; total: number }>(`/api/v1/runs${query ? `?${query}` : ''}`);
  },

  listByExperiment: (experimentId: string, params?: { status?: string; offset?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return registryApi.get<{ runs: Run[]; total: number }>(`/api/v1/experiments/${experimentId}/runs${query ? `?${query}` : ''}`);
  },

  get: (id: string) => registryApi.get<Run>(`/api/v1/runs/${id}`),

  getDetail: (id: string) => registryApi.get<RunDetail>(`/api/v1/runs/${id}/detail`),

  create: (experimentId: string, data: { name?: string; tags?: Record<string, string> }) =>
    registryApi.post<{ run: Run }>(`/api/v1/experiments/${experimentId}/runs`, data),

  end: (id: string, data: { status: 'completed' | 'failed' | 'killed' }) =>
    registryApi.post<{ run: Run }>(`/api/v1/runs/${id}/end`, data),

  delete: (id: string) => registryApi.delete(`/api/v1/runs/${id}`),

  // Metrics
  getMetrics: (id: string, params?: { keys?: string[] }) => {
    const queryParams = new URLSearchParams();
    if (params?.keys?.length) queryParams.append('keys', params.keys.join(','));
    const query = queryParams.toString();
    return registryApi.get<{ metrics: Metric[] }>(`/api/v1/runs/${id}/metrics${query ? `?${query}` : ''}`);
  },

  getMetricHistory: (id: string, key: string) =>
    registryApi.get<{ metrics: Metric[] }>(`/api/v1/runs/${id}/metrics/${encodeURIComponent(key)}/history`),

  getMultiRunMetricHistory: (runIds: string[], key: string) =>
    registryApi.post<{ history: Record<string, Metric[]> }>(`/api/v1/runs/metrics/${encodeURIComponent(key)}/multi`, { run_ids: runIds }),

  logMetrics: (id: string, metrics: { key: string; value: number; step?: number; timestamp?: number }[]) =>
    registryApi.post(`/api/v1/runs/${id}/metrics`, { metrics }),

  // Params
  getParams: (id: string) =>
    registryApi.get<{ params: Param[] }>(`/api/v1/runs/${id}/params`),

  logParams: (id: string, params: Record<string, string>) =>
    registryApi.post(`/api/v1/runs/${id}/params`, { params }),

  // Compare runs
  compare: (runIds: string[], metricKeys?: string[]) =>
    registryApi.post<RunComparison>('/api/v1/runs/compare', {
      run_ids: runIds,
      metric_keys: metricKeys,
    }),
};

// Model Registry Service
export const modelRegistryService = {
  // Model CRUD
  list: (params?: { offset?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return registryApi.get<{ models: RegisteredModel[]; total: number }>(`/api/v1/models${query ? `?${query}` : ''}`);
  },

  get: (id: string) => registryApi.get<{ model: RegisteredModel }>(`/api/v1/models/${id}`),

  getDetail: (id: string) => registryApi.get<ModelDetail>(`/api/v1/models/${id}/detail`),

  create: (data: { name: string; description?: string; tags?: Record<string, string>; project_id?: string }) => {
    const { project_id, ...body } = data;
    const config = project_id ? { headers: { 'X-Project-ID': project_id } } : undefined;
    return registryApi.post<{ model: RegisteredModel }>('/api/v1/models', body, config);
  },

  update: (id: string, data: { name?: string; description?: string; tags?: Record<string, string> }) =>
    registryApi.put<{ model: RegisteredModel }>(`/api/v1/models/${id}`, data),

  delete: (id: string) => registryApi.delete(`/api/v1/models/${id}`),

  // Model Versions
  listVersions: (modelId: string, params?: { offset?: number; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return registryApi.get<{ versions: ModelVersion[]; total: number }>(`/api/v1/models/${modelId}/versions${query ? `?${query}` : ''}`);
  },

  createVersion: (modelId: string, data: { run_id?: string; source?: string; description?: string }) =>
    registryApi.post<{ version: ModelVersion }>(`/api/v1/models/${modelId}/versions`, data),

  getVersion: (modelId: string, version: number) =>
    registryApi.get<{ version: ModelVersion }>(`/api/v1/models/${modelId}/versions/${version}`),

  updateVersion: (modelId: string, version: number, data: { description?: string; tags?: Record<string, string> }) =>
    registryApi.put<{ version: ModelVersion }>(`/api/v1/models/${modelId}/versions/${version}`, data),

  deleteVersion: (modelId: string, version: number) =>
    registryApi.delete(`/api/v1/models/${modelId}/versions/${version}`),

  // Stage Transitions
  transitionStage: (modelId: string, version: number, stage: 'development' | 'staging' | 'production' | 'archived', comment?: string) =>
    registryApi.put<{ version: ModelVersion }>(`/api/v1/models/${modelId}/versions/${version}/stage`, { stage, comment }),

  getModelTransitions: (modelId: string) =>
    registryApi.get<{ transitions: StageTransition[] }>(`/api/v1/models/${modelId}/transitions`),

  getVersionTransitions: (modelId: string, version: number) =>
    registryApi.get<{ transitions: StageTransition[] }>(`/api/v1/models/${modelId}/versions/${version}/transitions`),

  // Model Aliases
  setAlias: (modelId: string, data: { alias: string; version: number }) =>
    registryApi.post<{ alias: ModelAlias }>(`/api/v1/models/${modelId}/aliases`, data),

  getAliases: (modelId: string) =>
    registryApi.get<{ aliases: ModelAlias[] }>(`/api/v1/models/${modelId}/aliases`),

  deleteAlias: (modelId: string, alias: string) =>
    registryApi.delete(`/api/v1/models/${modelId}/aliases/${alias}`),

  getVersionByAlias: (modelId: string, alias: string) =>
    registryApi.get<{ version: ModelVersion }>(`/api/v1/models/${modelId}/alias/${alias}`),

  // Statistics
  getStats: () => registryApi.get<ModelStats>(`/api/v1/models/stats`),
};

// Artifact types
export interface ArtifactInfo {
  path: string;
  size: number;
  etag: string;
  last_modified: string;
}

// Artifact Service
export const artifactService = {
  listArtifacts: (runId: string) =>
    registryApi.get<{ artifacts: ArtifactInfo[] }>(`/api/v1/runs/${runId}/artifacts`),

  getDownloadUrl: (runId: string, artifactPath: string) =>
    registryApi.get<{ download_url: string; artifact_path: string }>(
      `/api/v1/runs/${runId}/artifacts/download-url`,
      { params: { artifact_path: artifactPath } }
    ),
};

// Dataset types
export interface Dataset {
  id: string;
  project_id: string;
  created_by_user_id: string;
  name: string;
  digest: string;
  source_type: string;
  source: string;
  schema: Record<string, string>;
  profile: Record<string, any>;
  tags: Record<string, string>;
  created_at: string;
  updated_at: string;
}

export interface VersionLineage {
  version: number;
  stage: string;
  run_id: string;
  run_name: string;
  datasets: Dataset[];
}

export interface ModelLineage {
  model_id: string;
  versions: VersionLineage[];
}

/** Parse JSON fields that may arrive as strings from the backend. */
const parseJsonField = <T>(value: any, fallback: T): T => {
  if (value === null || value === undefined) return fallback;
  if (typeof value === 'object') return value as T;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
};

export const normalizeDataset = (d: any): Dataset => ({
  ...d,
  schema: parseJsonField(d.schema, {}),
  profile: parseJsonField(d.profile, {}),
  tags: parseJsonField(d.tags, {}),
});

// Dataset Service
export const datasetService = {
  getRunDatasets: (runId: string) =>
    registryApi.get<{ datasets: Dataset[] }>(`/api/v1/runs/${runId}/datasets`),

  list: (params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString();
    return registryApi.get<{ datasets: Dataset[]; total: number }>(`/api/v1/datasets${query ? `?${query}` : ''}`);
  },

  get: (id: string) => registryApi.get<{ dataset: Dataset }>(`/api/v1/datasets/${id}`),

  getModelLineage: (modelId: string) =>
    registryApi.get<ModelLineage>(`/api/v1/models/${modelId}/lineage`),
};

// ── New types for 8 MLOps features ──────────────────────────────────────────

export interface RunAlert {
  id: string;
  run_id: string;
  project_id: string;
  metric_key: string;
  condition: 'gt' | 'lt' | 'gte' | 'lte' | 'eq';
  threshold: number;
  notify_email: string;
  notify_slack: string;
  status: 'active' | 'triggered';
  triggered_at: string | null;
  created_at: string;
}

export interface HyperparamSweep {
  id: string;
  project_id: string;
  experiment_id: string;
  created_by: string;
  name: string;
  search_space: string; // JSON string
  strategy: 'random' | 'grid';
  max_trials: number;
  metric: string;
  direction: 'minimize' | 'maximize';
  status: 'running' | 'completed' | 'stopped';
  best_trial_id: string;
  best_value: number | null;
  created_at: string;
  updated_at: string;
}

export interface SweepTrial {
  id: string;
  sweep_id: string;
  run_id: string;
  project_id: string;
  number: number;
  params: string; // JSON string
  status: 'running' | 'completed' | 'failed' | 'pruned';
  value: number | null;
  created_at: string;
  updated_at: string;
}

export interface ApprovalRequest {
  id: string;
  project_id: string;
  model_id: string;
  model_version_id: string;
  version: number;
  from_stage: string;
  to_stage: string;
  requested_by: string;
  reviewed_by: string;
  status: 'pending' | 'approved' | 'rejected' | 'superseded';
  comment: string;
  review_comment: string;
  reviewed_at: string | null;
  created_at: string;
}

export interface TrainingSchedule {
  id: string;
  project_id: string;
  experiment_id: string;
  created_by: string;
  name: string;
  cron_expr: string;
  run_name: string;
  run_config: string; // JSON string
  webhook_url: string;
  enabled: boolean;
  last_fired_at: string | null;
  next_fire_at: string | null;
  fire_count: number;
  created_at: string;
  updated_at: string;
}

// Alert Service
export const alertService = {
  create: (runId: string, data: {
    metric_key: string;
    condition: string;
    threshold: number;
    notify_email?: string;
    notify_slack?: string;
  }) => registryApi.post<{ alert: RunAlert }>(`/api/v1/runs/${runId}/alerts`, data),

  list: (runId: string) =>
    registryApi.get<{ alerts: RunAlert[] }>(`/api/v1/runs/${runId}/alerts`),

  delete: (alertId: string) =>
    registryApi.delete(`/api/v1/alerts/${alertId}`),
};

// Run notes update
export const runNotesService = {
  update: (runId: string, notes: string) =>
    registryApi.patch<{ notes: string }>(`/api/v1/runs/${runId}/notes`, { notes }),
};

// HPO Sweep Service
export const sweepService = {
  create: (experimentId: string, data: {
    name: string;
    search_space: Record<string, any>;
    strategy?: string;
    max_trials?: number;
    metric?: string;
    direction?: string;
  }) => registryApi.post<{ sweep: HyperparamSweep }>(`/api/v1/experiments/${experimentId}/sweeps`, data),

  list: (experimentId: string) =>
    registryApi.get<{ sweeps: HyperparamSweep[] }>(`/api/v1/experiments/${experimentId}/sweeps`),

  get: (sweepId: string) =>
    registryApi.get<{ sweep: HyperparamSweep }>(`/api/v1/sweeps/${sweepId}`),

  suggest: (sweepId: string) =>
    registryApi.get<{ params: Record<string, any> }>(`/api/v1/sweeps/${sweepId}/suggest`),

  createTrial: (sweepId: string, data: { run_id: string; params: Record<string, any> }) =>
    registryApi.post<{ trial: SweepTrial }>(`/api/v1/sweeps/${sweepId}/trials`, data),

  completeTrial: (sweepId: string, trialId: string, value: number) =>
    registryApi.post(`/api/v1/sweeps/${sweepId}/trials/${trialId}/complete`, { value }),

  listTrials: (sweepId: string) =>
    registryApi.get<{ trials: SweepTrial[] }>(`/api/v1/sweeps/${sweepId}/trials`),

  stop: (sweepId: string) =>
    registryApi.post(`/api/v1/sweeps/${sweepId}/stop`),
};

// Approval Service
export const approvalService = {
  listPending: () =>
    registryApi.get<{ approvals: ApprovalRequest[] }>('/api/v1/approvals/pending'),

  get: (requestId: string) =>
    registryApi.get<{ approval: ApprovalRequest }>(`/api/v1/approvals/${requestId}`),

  approve: (requestId: string, comment?: string) =>
    registryApi.post<{ approval: ApprovalRequest }>(`/api/v1/approvals/${requestId}/approve`, { comment }),

  reject: (requestId: string, comment?: string) =>
    registryApi.post<{ approval: ApprovalRequest }>(`/api/v1/approvals/${requestId}/reject`, { comment }),
};

// Schedule Service
export const scheduleService = {
  create: (data: {
    name: string;
    experiment_id: string;
    cron_expr: string;
    run_name?: string;
    run_config?: Record<string, any>;
    webhook_url?: string;
  }) => registryApi.post<{ schedule: TrainingSchedule }>('/api/v1/schedules', data),

  list: () =>
    registryApi.get<{ schedules: TrainingSchedule[] }>('/api/v1/schedules'),

  get: (scheduleId: string) =>
    registryApi.get<{ schedule: TrainingSchedule }>(`/api/v1/schedules/${scheduleId}`),

  update: (scheduleId: string, data: { enabled?: boolean; cron_expr?: string }) =>
    registryApi.patch<{ schedule: TrainingSchedule }>(`/api/v1/schedules/${scheduleId}`, data),

  delete: (scheduleId: string) =>
    registryApi.delete(`/api/v1/schedules/${scheduleId}`),
};

// ── Pipeline Orchestration types & service ──────────────────────────────

export interface Pipeline {
  id: string;
  project_id: string;
  created_by: string;
  name: string;
  description: string;
  steps_json: string;
  trigger_type: string;
  trigger_config: string;
  tags: string;
  status: 'active' | 'archived';
  run_count: number;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PipelineStepDef {
  id: string;
  name: string;
  type: 'training' | 'evaluation' | 'deployment' | 'custom';
  depends_on: string[];
  config: Record<string, any>;
  timeout_minutes: number;
  retry_count: number;
}

export interface PipelineRun {
  id: string;
  pipeline_id: string;
  project_id: string;
  triggered_by: string;
  trigger_reason: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  steps_snapshot: string;
  temporal_workflow_id: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number;
  error: string;
  created_at: string;
  updated_at: string;
}

export interface PipelineStepExecution {
  id: string;
  pipeline_run_id: string;
  project_id: string;
  step_id: string;
  step_name: string;
  step_type: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
  run_id: string;
  model_version_id: string;
  deployment_id: string;
  logs_path: string;
  started_at: string | null;
  completed_at: string | null;
  duration_ms: number;
  error: string;
  created_at: string;
  updated_at: string;
}

export const pipelineService = {
  create: (data: {
    name: string;
    description?: string;
    steps: PipelineStepDef[];
    trigger_type?: string;
    trigger_config?: Record<string, any>;
    tags?: Record<string, string>;
  }) => registryApi.post<{ pipeline: Pipeline }>('/api/v1/pipelines', data),

  list: (params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString();
    return registryApi.get<{ pipelines: Pipeline[]; total: number }>(`/api/v1/pipelines${query ? `?${query}` : ''}`);
  },

  get: (id: string) => registryApi.get<{ pipeline: Pipeline }>(`/api/v1/pipelines/${id}`),

  update: (id: string, data: {
    name?: string;
    description?: string;
    steps?: PipelineStepDef[];
    trigger_type?: string;
    trigger_config?: Record<string, any>;
    tags?: Record<string, string>;
  }) => registryApi.put<{ pipeline: Pipeline }>(`/api/v1/pipelines/${id}`, data),

  archive: (id: string) => registryApi.delete(`/api/v1/pipelines/${id}`),

  triggerRun: (pipelineId: string, triggerReason?: string) =>
    registryApi.post<{ run: PipelineRun }>(`/api/v1/pipelines/${pipelineId}/runs`, { trigger_reason: triggerReason }),

  listRuns: (pipelineId: string, params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString();
    return registryApi.get<{ runs: PipelineRun[]; total: number }>(`/api/v1/pipelines/${pipelineId}/runs${query ? `?${query}` : ''}`);
  },

  getRun: (runId: string) =>
    registryApi.get<{ run: PipelineRun; steps: PipelineStepExecution[] }>(`/api/v1/pipeline-runs/${runId}`),

  cancelRun: (runId: string) =>
    registryApi.post(`/api/v1/pipeline-runs/${runId}/cancel`),
};

// ── Model Monitoring types & service ────────────────────────────────────

export interface ModelMonitor {
  id: string;
  project_id: string;
  model_id: string;
  model_version_id: string;
  name: string;
  endpoint_url: string;
  status: 'active' | 'paused' | 'archived';
  window_size: string;
  retention_days: number;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface PerformanceSnapshot {
  id: string;
  monitor_id: string;
  project_id: string;
  window_start: string;
  window_end: string;
  request_count: number;
  error_count: number;
  error_rate: number;
  avg_latency_ms: number;
  p50_latency_ms: number;
  p95_latency_ms: number;
  p99_latency_ms: number;
  accuracy_samples: number;
  accuracy: number | null;
  created_at: string;
}

export interface MonitorAlertRule {
  id: string;
  monitor_id: string;
  project_id: string;
  metric_key: string;
  condition: 'gt' | 'lt' | 'gte' | 'lte';
  threshold: number;
  window_size: string;
  notify_email: string;
  notify_webhook: string;
  auto_action: string;
  auto_action_config: string;
  status: 'active' | 'triggered' | 'resolved' | 'disabled';
  last_triggered_at: string | null;
  created_at: string;
  updated_at: string;
}

export const monitoringService = {
  create: (data: {
    model_id: string;
    model_version_id: string;
    name: string;
    endpoint_url?: string;
    window_size?: string;
    retention_days?: number;
  }) => registryApi.post<{ monitor: ModelMonitor }>('/api/v1/monitors', data),

  list: (params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString();
    return registryApi.get<{ monitors: ModelMonitor[]; total: number }>(`/api/v1/monitors${query ? `?${query}` : ''}`);
  },

  get: (id: string) =>
    registryApi.get<{ monitor: ModelMonitor; latest_metrics: PerformanceSnapshot | null }>(`/api/v1/monitors/${id}`),

  archive: (id: string) => registryApi.delete(`/api/v1/monitors/${id}`),

  ingest: (monitorId: string, predictions: {
    input_hash?: string;
    output_summary?: Record<string, any>;
    latency_ms: number;
    status_code: number;
    feature_values?: Record<string, any>;
    timestamp?: string;
  }[]) => registryApi.post<{ ingested: number }>(`/api/v1/monitors/${monitorId}/ingest`, { predictions }),

  submitGroundTruth: (monitorId: string, entries: { input_hash: string; ground_truth: any }[]) =>
    registryApi.post<{ updated: number }>(`/api/v1/monitors/${monitorId}/ground-truth`, { entries }),

  getMetrics: (monitorId: string, params?: { start?: string; end?: string; limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.start) queryParams.append('start', params.start);
    if (params?.end) queryParams.append('end', params.end);
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    const query = queryParams.toString();
    return registryApi.get<{ snapshots: PerformanceSnapshot[] }>(`/api/v1/monitors/${monitorId}/metrics${query ? `?${query}` : ''}`);
  },

  getLatestMetrics: (monitorId: string) =>
    registryApi.get<{ snapshot: PerformanceSnapshot }>(`/api/v1/monitors/${monitorId}/metrics/latest`),

  createAlert: (monitorId: string, data: {
    metric_key: string;
    condition: string;
    threshold: number;
    window_size?: string;
    notify_email?: string;
    notify_webhook?: string;
    auto_action?: string;
    auto_action_config?: Record<string, any>;
  }) => registryApi.post<{ alert: MonitorAlertRule }>(`/api/v1/monitors/${monitorId}/alerts`, data),

  listAlerts: (monitorId: string) =>
    registryApi.get<{ alerts: MonitorAlertRule[] }>(`/api/v1/monitors/${monitorId}/alerts`),

  updateAlert: (alertId: string, data: Partial<MonitorAlertRule>) =>
    registryApi.put<{ alert: MonitorAlertRule }>(`/api/v1/monitor-alerts/${alertId}`, data),

  deleteAlert: (alertId: string) =>
    registryApi.delete(`/api/v1/monitor-alerts/${alertId}`),
};

// ── Drift Detection types & service ─────────────────────────────────────

export interface DriftMonitor {
  id: string;
  monitor_id: string;
  project_id: string;
  name: string;
  baseline_type: string;
  baseline_path: string;
  features: string;
  method: string;
  threshold: number;
  check_schedule: string;
  sample_size: number;
  auto_retrain: boolean;
  pipeline_id: string;
  status: 'active' | 'paused';
  last_check_at: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface DriftReport {
  id: string;
  drift_monitor_id: string;
  project_id: string;
  drift_detected: boolean;
  overall_score: number;
  feature_scores: string;
  sample_count: number;
  baseline_count: number;
  concept_drift: boolean | null;
  accuracy_baseline: number | null;
  accuracy_current: number | null;
  action_taken: string;
  pipeline_run_id: string;
  report_path: string;
  checked_at: string;
  created_at: string;
}

export const driftService = {
  create: (data: {
    monitor_id: string;
    name: string;
    baseline_type?: string;
    features?: string[];
    method?: string;
    threshold?: number;
    check_schedule?: string;
    sample_size?: number;
    auto_retrain?: boolean;
    pipeline_id?: string;
  }) => registryApi.post<{ drift_monitor: DriftMonitor }>('/api/v1/drift-monitors', data),

  list: (params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString();
    return registryApi.get<{ drift_monitors: DriftMonitor[]; total: number }>(`/api/v1/drift-monitors${query ? `?${query}` : ''}`);
  },

  get: (id: string) =>
    registryApi.get<{ drift_monitor: DriftMonitor; latest_report: DriftReport | null }>(`/api/v1/drift-monitors/${id}`),

  update: (id: string, data: Partial<DriftMonitor>) =>
    registryApi.put<{ drift_monitor: DriftMonitor }>(`/api/v1/drift-monitors/${id}`, data),

  archive: (id: string) => registryApi.delete(`/api/v1/drift-monitors/${id}`),

  uploadBaseline: (id: string, baselinePath: string) =>
    registryApi.post<{ drift_monitor: DriftMonitor }>(`/api/v1/drift-monitors/${id}/baseline`, { baseline_path: baselinePath }),

  getBaseline: (id: string) =>
    registryApi.get<{ baseline_type: string; baseline_path: string }>(`/api/v1/drift-monitors/${id}/baseline`),

  listReports: (driftMonitorId: string, params?: { limit?: number; offset?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params?.offset !== undefined) queryParams.append('offset', params.offset.toString());
    const query = queryParams.toString();
    return registryApi.get<{ reports: DriftReport[]; total: number }>(`/api/v1/drift-monitors/${driftMonitorId}/reports${query ? `?${query}` : ''}`);
  },

  getReport: (reportId: string) =>
    registryApi.get<{ report: DriftReport }>(`/api/v1/drift-reports/${reportId}`),

  triggerCheck: (driftMonitorId: string) =>
    registryApi.post<{ report: DriftReport }>(`/api/v1/drift-monitors/${driftMonitorId}/check`),
};

// Dashboard/Visualization Service
export const dashboardService = {
  getStats: () =>
    registryApi.get<{
      total_experiments: number;
      total_runs: number;
      total_models: number;
      recent_runs: Run[];
      runs_by_status: Record<string, number>;
    }>('/api/v1/dashboard/stats'),
};
